import { codeFont, numFont, plateFont } from '../../utils/fonts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LockIcon from '@mui/icons-material/Lock';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BlockIcon from '@mui/icons-material/Block';
import SaveIcon from '@mui/icons-material/Save';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../utils/languages';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { getOrganizationId, useUserStore } from '../../store';
import PartAutocomplete from '../shared/PartAutocomplete';
import DecimalField from '../shared/DecimalField';
import VatPriceField from '../shared/VatPriceField';
import TargetLabelField from './TargetLabelField';
import SaleLineDialog from './SaleLineDialog';
import KoontilaskuDialog from './KoontilaskuDialog';
import LaheteCartImportDialog from './LaheteCartImportDialog';
import SupplierOrderDialog from '../maintenances/SupplierOrderDialog';
import CustomerPurchaseHistoryDialog from '../customers/CustomerPurchaseHistoryDialog';
import {
  lineDiscountAmount,
  partsSaleErrorMessage,
  serverLineDisplay,
  totalDiscountAmount,
  totalMargin,
} from './salesUtils';
import { can } from '../../utils/access';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { parseISO, isValid, format as formatDateFns } from 'date-fns';
import PartsSaleStatusBadge from './PartsSaleStatusBadge';
import { formatDate, formatQuantity } from '../../utils';
import { calcPriceExclVat, formatPriceWithCurrency, roundMoney } from '../../utils/vat';

function SumRow({ label, value, muted }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color={muted ? 'text.secondary' : 'text.primary'}>{label}</Typography>
      <Typography variant="body2" sx={{ ...numFont }} color={muted ? 'text.secondary' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

const emptyFreeLine = { description: '', quantity: '1', unit_price: '', withVat: null, margin: false, purchase_price: '' };

/**
 * One lähete (delivery-note tab), per claude_designs/osamyynti: customer card,
 * lines with handover timestamps + stock hints + qty steppers, VAT summary
 * rail, print, invoicing, cancel and seller notes. Invoiced notes are locked —
 * corrections go through a credit note.
 */
function LahetteDetailView() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const vatSuffix = (inclVat) => t(inclVat ? 'incl. VAT' : 'excl. VAT');
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addMode, setAddMode] = useState('none'); // none | part | free
  const [addSpecial, setAddSpecial] = useState(false); // erikoistilaus: order now, hand over on arrival
  const [markOrderedLine, setMarkOrderedLine] = useState(null);
  const [orderForm, setOrderForm] = useState({ reference: '', expected: '' });
  // Supplier ordering (Varaosatilaukset): one "Tilaa <toimittaja>" banner per integration
  // the to_order lines resolve to, plus the vendor basket import.
  const [supplierOrderGroup, setSupplierOrderGroup] = useState(null);
  const [cartImportOpen, setCartImportOpen] = useState(false);
  const [freeLine, setFreeLine] = useState(emptyFreeLine);
  const [editLine, setEditLine] = useState(null);
  // Memoised: the dialog re-seeds its state whenever this object changes, and a
  // fresh literal on every parent render (snackbar, flash timer, submitting)
  // would wipe the seller's unsaved edits mid-dialog.
  const editInitialLine = useMemo(
    () =>
      editLine
        ? {
            key: String(editLine.id),
            part_id: editLine.part_id,
            description: editLine.part_name || editLine.description,
            target_label: editLine.target_label || '',
            quantity: String(editLine.quantity),
            unit_price: String(
              editLine.is_margin_vat_scheme
                ? editLine.unit_price_incl_vat || editLine.unit_price
                : editLine.effective_price
            ),
            unit_price_incl_vat:
              editLine.unit_price_incl_vat != null ? String(editLine.unit_price_incl_vat) : null,
            price_entered_with_vat: !!editLine.price_entered_with_vat,
            vat_rate: parseFloat(editLine.vat_rate) || 25.5,
            is_margin_vat_scheme: editLine.is_margin_vat_scheme,
            discount_percent: String(editLine.discount_percent || '0'),
            // Undefined when the user may not see purchase prices — the
            // dialog then hides the kate block instead of showing 100 %.
            cost_price: editLine.cost_price != null ? String(editLine.cost_price) : null,
            margin_vat_purchase_price:
              editLine.margin_vat_purchase_price != null
                ? String(editLine.margin_vat_purchase_price)
                : null,
            unit: editLine.unit,
          }
        : null,
    [editLine]
  );

  const [submitting, setSubmitting] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  // Kohde (target vehicle/machine): the header default prefills `addTarget`, which the
  // add-line forms send with every new line. Existing lines keep their own value.
  const [targetDraft, setTargetDraft] = useState('');
  const [targetDirty, setTargetDirty] = useState(false);
  const [addTarget, setAddTarget] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [flashLine, setFlashLine] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const notesLoadedRef = useRef(false);

  const fetchSale = useCallback(async () => {
    try {
      const resp = await axios.get(`/api/parts-sales/${id}/`);
      setSale(resp.data);
      if (!notesLoadedRef.current) {
        setNotesDraft(resp.data.notes || '');
        setTargetDraft(resp.data.target_label || '');
        setAddTarget(resp.data.target_label || '');
        notesLoadedRef.current = true;
      }
    } catch (e) {
      setSnack({ open: true, message: t('parts_sales.loadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    setLoading(true);
    notesLoadedRef.current = false;
    fetchSale();
  }, [fetchSale]);

  const isOpen = sale?.status === 'open';
  const isInvoiced = sale?.status === 'invoiced';
  const isCancelled = sale?.status === 'cancelled';

  const apiError = (e, fallbackKey) => {
    setSnack({ open: true, message: partsSaleErrorMessage(e, t, fallbackKey), severity: 'error' });
  };

  const flash = (lineId) => {
    setFlashLine(lineId);
    setTimeout(() => setFlashLine(null), 700);
  };

  const addPartLine = async (part, meta = {}) => {
    // Auto-tilattava nudge: a catalogue pick with no stock on the shelf, or a
    // tracked part with zero free saldo, is almost certainly ordered-in — add it
    // as a special order and flip the toggle so the cashier sees (and can undo
    // for the next line). A stocked catalogue match with free saldo stays normal.
    const free = part.stock_info != null ? parseFloat(part.stock_info.quantity_free) : null;
    const nudge = !addSpecial && ((meta.fromCatalogue && !(free > 0)) || (free != null && free <= 0));
    const special = addSpecial || nudge;
    setSubmitting(true);
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/add_line/`, {
        part_id: part.id,
        quantity: '1',
        ...(special ? { special_order: true } : {}),
        ...(addTarget.trim() ? { target_label: addTarget.trim() } : {}),
      });
      setSale(resp.data);
      const newest = resp.data.lines[resp.data.lines.length - 1];
      if (newest) flash(newest.id);
      if (nudge) {
        setAddSpecial(true);
        setSnack({ open: true, message: t('parts_sales.specialOrderAutoOn'), severity: 'info' });
      }
    } catch (e) {
      apiError(e, 'parts_sales.addLineFailed');
    } finally {
      setSubmitting(false);
    }
  };

  // Free-line price entry mode: seeded from the org display default, per-line override.
  const freeLineWithVat = freeLine.margin ? true : (freeLine.withVat ?? orgDefaultVat);

  const addFreeLine = async () => {
    if (!freeLine.description.trim()) return;
    setSubmitting(true);
    try {
      const entered = parseFloat(freeLine.unit_price) || 0;
      const rate = 25.5;
      const payload = {
        description: freeLine.description.trim(),
        quantity: freeLine.quantity || '1',
        // Margin lines: entered price IS the gross. VAT-inclusive entry: send the
        // exact gross as source of truth + a derived net. Otherwise plain net.
        unit_price: freeLine.margin || !freeLineWithVat
          ? freeLine.unit_price || '0'
          : String(roundMoney(calcPriceExclVat(entered, rate))),
        ...(!freeLine.margin && freeLineWithVat
          ? { unit_price_incl_vat: freeLine.unit_price || '0', price_entered_with_vat: true }
          : {}),
        is_margin_vat_scheme: freeLine.margin,
        ...(freeLine.margin && freeLine.purchase_price ? { margin_vat_purchase_price: freeLine.purchase_price } : {}),
        ...(addSpecial ? { special_order: true } : {}),
        ...(addTarget.trim() ? { target_label: addTarget.trim() } : {}),
      };
      const resp = await axios.post(`/api/parts-sales/${id}/add_line/`, payload);
      setSale(resp.data);
      setFreeLine(emptyFreeLine);
      setAddMode('none');
    } catch (e) {
      apiError(e, 'parts_sales.addLineFailed');
    } finally {
      setSubmitting(false);
    }
  };

  const stepQty = async (line, delta) => {
    const next = (parseFloat(line.quantity) || 0) + delta;
    if (next < 1) return;
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/update_line/`, { line_id: line.id, quantity: String(next) });
      setSale(resp.data);
    } catch (e) {
      apiError(e, 'parts_sales.updateLineFailed');
    }
  };

  const submitLineEdit = async (spec) => {
    setSubmitting(true);
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/update_line/`, {
        line_id: editLine.id,
        quantity: spec.quantity,
        unit_price: spec.unit_price,
        ...(spec.price_entered_with_vat && spec.unit_price_incl_vat != null
          ? { unit_price_incl_vat: spec.unit_price_incl_vat, price_entered_with_vat: true }
          : {}),
        discount_percent: spec.discount_percent,
        // Part rows carry an editable description too (it is what prints on the
        // lähete and the invoice), so it can no longer be dropped here.
        description: spec.description,
        target_label: spec.target_label ?? '',
      });
      setSale(resp.data);
      setEditLine(null);
    } catch (e) {
      apiError(e, 'parts_sales.updateLineFailed');
    } finally {
      setSubmitting(false);
    }
  };

  const openMarkOrdered = (line) => {
    setOrderForm({ reference: line.supplier_reference || '', expected: line.expected_date || '' });
    setMarkOrderedLine(line);
  };

  const submitMarkOrdered = async () => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/order_line/`, {
        line_id: markOrderedLine.id,
        supplier_reference: orderForm.reference,
        ...(orderForm.expected ? { expected_date: orderForm.expected } : {}),
      });
      setSale(resp.data);
      setMarkOrderedLine(null);
    } catch (e) {
      apiError(e, 'parts_sales.orderLineFailed');
    }
  };

  const markArrived = async (line) => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/mark_arrived/`, { line_id: line.id });
      setSale(resp.data.sale);
      if (resp.data.sms_sent) {
        setSnack({ open: true, message: t('parts_sales.smsSent'), severity: 'success' });
      } else {
        setSnack({
          open: true,
          message: `${t('parts_sales.smsNotSent')}: ${t(`parts_sales.smsReason_${resp.data.sms_reason}`)}`,
          severity: 'warning',
        });
      }
    } catch (e) {
      apiError(e, 'parts_sales.arriveFailed');
    }
  };

  const handOverLine = async (line) => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/hand_over/`, { line_id: line.id });
      setSale(resp.data);
      setSnack({ open: true, message: t('parts_sales.handedOverNow'), severity: 'success' });
    } catch (e) {
      apiError(e, 'parts_sales.handOverFailed');
    }
  };

  const returnLine = async (lineId) => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/return_line/`, { line_id: lineId });
      setSale(resp.data);
      setSnack({ open: true, message: t('parts_sales.lineReturned'), severity: 'success' });
    } catch (e) {
      apiError(e, 'parts_sales.returnFailed');
    }
  };

  const cancelSale = async () => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/cancel/`);
      setSale(resp.data);
      setCancelOpen(false);
      setSnack({ open: true, message: t('parts_sales.cancelled'), severity: 'success' });
    } catch (e) {
      setCancelOpen(false);
      apiError(e, 'parts_sales.cancelFailed');
    }
  };

  const saveNotes = async () => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/set_notes/`, { notes: notesDraft });
      setSale(resp.data);
      setNotesDirty(false);
      setSnack({ open: true, message: t('parts_sales.notesSaved'), severity: 'success' });
    } catch (e) {
      apiError(e, 'parts_sales.notesSaveFailed');
    }
  };

  const saveTarget = async () => {
    try {
      const resp = await axios.post(`/api/parts-sales/${id}/set_target_label/`, { target_label: targetDraft.trim() });
      setSale(resp.data);
      setTargetDraft(resp.data.target_label || '');
      setAddTarget(resp.data.target_label || '');
      setTargetDirty(false);
      setSnack({ open: true, message: t('parts_sales.targetSaved'), severity: 'success' });
    } catch (e) {
      apiError(e, 'parts_sales.targetSaveFailed');
    }
  };

  const printPdf = (language) => {
    setMenuAnchor(null);
    const orgId = getOrganizationId();
    window.open(`/api/parts-sales/${id}/pdf/?preview=true&language=${language}&organization=${orgId}`, '_blank');
  };

  if (loading || !sale) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const uiLang = normalizeLanguage(i18n.language);
  const activeLines = sale.lines.filter((l) => l.status === 'active');
  const initials = (sale.customer_name || '—').slice(0, 2).toUpperCase();

  // Same rule as the work order's banner: lines whose part orders through an
  // integration, not yet on an active order, not skipped, still waiting to be ordered.
  const canOrderFromSuppliers = can(currentOrganization, 'supplier_orders.order');
  const supplierOrderGroups = (() => {
    if (!canOrderFromSuppliers || isCancelled) return [];
    const groups = {};
    activeLines.forEach((l) => {
      if (
        l.fulfillment_status === 'to_order' &&
        l.supplier_integration_id &&
        !l.is_supplier_ordered &&
        !l.supplier_order_skipped &&
        l.supplier_supports_ordering !== false
      ) {
        if (!groups[l.supplier_integration_id]) {
          groups[l.supplier_integration_id] = {
            integrationId: l.supplier_integration_id,
            integrationName: l.supplier_display_name,
            lines: [],
          };
        }
        groups[l.supplier_integration_id].lines.push(l);
      }
    });
    return Object.values(groups);
  })();
  // The shared order dialog shows part rows the way the work order does.
  const dialogRows = (lines) =>
    lines.map((l) => ({
      id: l.id,
      name: l.part_name || l.description,
      primary_code: l.part_code,
      quantity: l.quantity,
      cost_price: l.cost_price,
    }));

  // Kate needs the cost, which the API strips without costs.view_purchase_prices.
  const canViewMargins =
    can(currentOrganization, 'costs.view_purchase_prices') && can(currentOrganization, 'costs.view_margins');
  const totalDiscount = totalDiscountAmount(activeLines, orgDefaultVat);
  const saleMargin = canViewMargins ? totalMargin(activeLines) : null;

  // The row shows the à-hinta BEFORE the discount and the total AFTER it, so
  // without this caption "55,59 € × 1 = 38,91 €" is all the customer's copy and
  // the screen ever say about where the money went.
  // A part row is titled by the part name, but its description is what actually
  // prints on the lähete and the invoice. When the seller has made it say
  // something else, the screen has to show that too — otherwise the note he typed
  // is invisible everywhere except the customer's copy.
  const rowNote = (line) =>
    line.description && line.description !== line.part_name ? line.description : null;

  const discountCaption = (line) => {
    const pct = parseFloat(line.discount_percent) || 0;
    if (pct <= 0) return null;
    const amount = lineDiscountAmount(line, orgDefaultVat);
    const pctLabel = `−${formatQuantity(pct)} %`;
    return amount ? `${pctLabel} (−${formatPriceWithCurrency(amount)})` : pctLabel;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1240, mx: 'auto' }} data-testid="lahete-detail-view">
      {/* Breadcrumb + header */}
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/lahetteet" underline="hover" color="text.secondary">
          {t('parts_sales.deliveries')}
        </Link>
        <Typography color="text.primary" sx={{ ...numFont }}>#{sale.number}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
              {t('parts_sales.lahete')}{' '}
              <Box component="span" sx={{ ...numFont }}>#{sale.number}</Box>
            </Typography>
            <PartsSaleStatusBadge status={sale.status} size="medium" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('parts_sales.created')} {formatDate(sale.created_at)}
            {' · '}
            {isInvoiced ? t('parts_sales.invoicedSubtitle') : t('parts_sales.laheteSubtitle')}
            {sale.created_by_name ? ` · ${sale.created_by_name}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {isInvoiced && sale.invoice && (
            <Button
              variant="outlined"
              startIcon={<ReceiptLongIcon />}
              onClick={() => navigate(`/invoices/${sale.invoice}`)}
              data-testid="lahete-open-invoice"
              sx={{ borderRadius: '8px' }}
            >
              {t('parts_sales.openInvoice')} {sale.invoice_number}
            </Button>
          )}
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => printPdf(uiLang)} data-testid="lahete-print" sx={{ borderRadius: '8px' }}>
            {t('parts_sales.printLahete')}
          </Button>
          {isOpen && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ReceiptLongIcon />}
              disabled={activeLines.length === 0}
              onClick={() => setInvoiceOpen(true)}
              data-testid="lahete-invoice"
              sx={{ borderRadius: '8px' }}
            >
              {t('parts_sales.invoiceAction')}
            </Button>
          )}
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} data-testid="lahete-menu">
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={() => printPdf(uiLang === 'fi' ? 'en' : 'fi')}>
              <PrintIcon fontSize="small" sx={{ mr: 1 }} />
              {uiLang === 'fi' ? t('parts_sales.printInEnglish') : t('parts_sales.printInFinnish')}
            </MenuItem>
            {isOpen && (
              <MenuItem onClick={() => { setMenuAnchor(null); setCancelOpen(true); }} sx={{ color: 'error.main' }} data-testid="lahete-cancel">
                <BlockIcon fontSize="small" sx={{ mr: 1 }} />
                {t('parts_sales.cancelLahete')}
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {/* minmax(0, …) so the lines table can't force the tracks (and the page)
          wider than the viewport — it scrolls inside its own wrapper instead. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        {/* LEFT */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Customer card */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontWeight: 600 }}>{initials}</Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 600 }} noWrap>{sale.customer_name}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {sale.customer_type === 'company' ? t('parts_sales.billingCustomer') : t('parts_sales.privateCustomer')}
                  {' · '}
                  {t('parts_sales.paymentTerms')} {sale.payment_terms_days} {t('parts_sales.daysNet')}
                  {sale.customer_discount_percent && parseFloat(sale.customer_discount_percent) > 0
                    ? ` · ${t('parts_sales.partsDiscount')} ${formatQuantity(sale.customer_discount_percent)} %`
                    : ''}
                </Typography>
                {(sale.customer_phone || sale.customer_email) && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {[sale.customer_phone, sale.customer_email].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
              {sale.customer_type === 'company' && (
                <Chip
                  size="small"
                  icon={<AccountBalanceIcon sx={{ fontSize: 14 }} />}
                  label={t('parts_sales.onAccount')}
                  sx={{ bgcolor: '#ebf4ff', color: '#2b6cb0', fontWeight: 600 }}
                />
              )}
              <Tooltip title={t('Purchase history')}>
                <IconButton size="small" onClick={() => setHistoryOpen(true)} data-testid="lahete-customer-history">
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {/* Locked / cancelled banner */}
          {isInvoiced && (
            <Alert icon={<LockIcon fontSize="small" />} severity="info" sx={{ borderRadius: '10px', bgcolor: '#f5f0ff', color: '#553c9a', '& .MuiAlert-icon': { color: '#553c9a' } }}>
              {t('parts_sales.lockedBanner')}
            </Alert>
          )}
          {isCancelled && (
            <Alert severity="warning" sx={{ borderRadius: '10px' }}>
              {t('parts_sales.cancelledBanner')}
            </Alert>
          )}

          {/* Lines */}
          <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 600 }}>
                {t('parts_sales.lines')} · {activeLines.length}
              </Typography>
              {isOpen && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant={addMode === 'part' ? 'contained' : 'text'}
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => setAddMode(addMode === 'part' ? 'none' : 'part')}
                    data-testid="lahete-add-part"
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    {t('parts_sales.addPart')}
                  </Button>
                  {canOrderFromSuppliers && (
                    <Button
                      size="small"
                      variant="text"
                      color="secondary"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => setCartImportOpen(true)}
                      data-testid="lahete-import-cart"
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      {t('parts_sales.importCart')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant={addMode === 'free' ? 'contained' : 'text'}
                    color="secondary"
                    startIcon={<EditNoteIcon />}
                    onClick={() => setAddMode(addMode === 'free' ? 'none' : 'free')}
                    data-testid="lahete-free-line"
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    {t('parts_sales.freeTextLine')}
                  </Button>
                </Box>
              )}
            </Box>

            {/* Special-order toggle for both add modes */}
            {isOpen && addMode !== 'none' && (
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={addSpecial}
                      onChange={(e) => setAddSpecial(e.target.checked)}
                      data-testid="lahete-special-toggle"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500 }}>{t('parts_sales.specialOrderToggle')}</Typography>}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('parts_sales.specialOrderHint')}
                </Typography>
                {addSpecial && !sale.customer_phone && (
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                    {t('parts_sales.noPhoneWarning')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Add-part search */}
            {addMode === 'part' && isOpen && (
              <Box sx={{ px: 2, py: 1.75, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <PartAutocomplete
                  onSelect={addPartLine}
                  placeholder={t('parts_sales.scanOrPickForLahete')}
                  autoFocus
                  sourceToggle
                  catalogueFilters
                  filtersCollapsible
                  stockMatchCheck
                  inputTestId="lahete-part-input"
                />
                <TargetLabelField
                  value={addTarget}
                  onChange={setAddTarget}
                  customerId={sale.customer}
                  label={t('parts_sales.target')}
                  placeholder={t('parts_sales.targetPlaceholder')}
                  inputTestId="lahete-add-target"
                  fullWidth={false}
                  sx={{ mt: 1.25, width: { xs: '100%', sm: 300 } }}
                />
              </Box>
            )}

            {/* Orderable lines: one banner per supplier, as on the work order */}
            {supplierOrderGroups.length > 0 && (
              <Box sx={{ px: 2, pt: 1.5 }}>
                {supplierOrderGroups.map((group) => (
                  <Box
                    key={group.integrationId}
                    data-testid={`lahete-supplier-order-${group.integrationId}`}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      flexWrap: 'wrap', gap: 1, p: 1.5,
                      bgcolor: '#f0f7ff', borderRadius: '8px', mb: 1,
                      border: 1, borderColor: '#c2dbf4',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShippingIcon sx={{ fontSize: 18, color: 'info.main' }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {t('supplierOrder.partsOrderable', { count: group.lines.length, supplier: group.integrationName })}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setSupplierOrderGroup(group)}
                      data-testid={`lahete-order-supplier-${group.integrationId}`}
                      sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                    >
                      {t('supplierOrder.orderButton', { supplier: group.integrationName })}
                    </Button>
                  </Box>
                ))}
              </Box>
            )}

            {/* Free-line form */}
            {addMode === 'free' && isOpen && (
              <Box sx={{ px: 2, py: 1.75, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField
                  label={t('parts_sales.description')}
                  value={freeLine.description}
                  onChange={(e) => setFreeLine({ ...freeLine, description: e.target.value })}
                  placeholder={t('parts_sales.freeLinePlaceholder')}
                  size="small"
                  autoFocus
                  sx={{ flex: '2 1 220px' }}
                />
                <TargetLabelField
                  value={addTarget}
                  onChange={setAddTarget}
                  customerId={sale.customer}
                  label={t('parts_sales.target')}
                  placeholder={t('parts_sales.targetPlaceholder')}
                  inputTestId="lahete-free-target"
                  fullWidth={false}
                  sx={{ flex: '1 1 180px', maxWidth: 260 }}
                />
                <DecimalField
                  label={t('parts_sales.qty')}
                  value={freeLine.quantity}
                  onChange={(v) => setFreeLine({ ...freeLine, quantity: v })}
                  size="small"
                  sx={{ width: 80 }}
                />
                <VatPriceField
                  value={freeLine.unit_price}
                  onChange={(v) => setFreeLine({ ...freeLine, unit_price: v })}
                  withVat={freeLineWithVat}
                  onModeChange={(newWithVat, nextValue) =>
                    setFreeLine({ ...freeLine, withVat: newWithVat, unit_price: nextValue })
                  }
                  lockedInclVat={freeLine.margin}
                  allowNegative={!freeLine.margin}
                  vatRate={25.5}
                  sx={{ width: 240 }}
                />
                <FormControlLabel
                  sx={{ mt: 0.25 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={freeLine.margin}
                      onChange={(e) => setFreeLine({ ...freeLine, margin: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">{t('parts_sales.marginVatScheme')}</Typography>}
                />
                {freeLine.margin && (
                  <DecimalField
                    label={t('parts_sales.purchasePrice')}
                    value={freeLine.purchase_price}
                    onChange={(v) => setFreeLine({ ...freeLine, purchase_price: v })}
                    size="small"
                    InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                    sx={{ width: 130 }}
                  />
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!freeLine.description.trim() || submitting}
                  onClick={addFreeLine}
                  data-testid="lahete-free-line-add"
                  sx={{ borderRadius: '8px', mt: 0.25 }}
                >
                  {t('parts_sales.add')}
                </Button>
              </Box>
            )}

            {sale.lines.length === 0 ? (
              <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
                <PlaylistAddIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                <Typography sx={{ fontWeight: 600, mt: 1 }}>{t('parts_sales.noLinesYet')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('parts_sales.noLinesHint')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('parts_sales.descriptionCol')}</TableCell>
                      <TableCell>{t('parts_sales.handedOver')}</TableCell>
                      <TableCell align="right">{t('parts_sales.qty')}</TableCell>
                      <TableCell align="right">
                        {t('parts_sales.unitPriceShort')}{' '}
                        <Typography component="span" variant="caption" color="text.disabled">
                          {vatSuffix(orgDefaultVat)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {orgDefaultVat ? `${t('parts_sales.total')} ` : `${t('parts_sales.netCol')} `}
                        <Typography component="span" variant="caption" color="text.disabled">
                          {vatSuffix(orgDefaultVat)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sale.lines.map((l) => {
                      const returned = l.status === 'returned';
                      return (
                        <TableRow
                          key={l.id}
                          sx={{
                            bgcolor: flashLine === l.id ? '#ebf4ff' : 'transparent',
                            transition: 'background 600ms',
                            ...(returned ? { opacity: 0.55 } : {}),
                            '&:last-child td': { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={{ py: 1.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, ...(returned ? { textDecoration: 'line-through' } : {}) }}>
                                {l.part_name || l.description}
                              </Typography>
                              {!l.part_id && (
                                <Chip size="small" variant="outlined" icon={<EditNoteIcon sx={{ fontSize: 13 }} />} label={t('parts_sales.freeTextTag')} sx={{ height: 20, fontSize: 10.5 }} />
                              )}
                              {l.is_margin_vat_scheme && (
                                <Chip size="small" variant="outlined" color="warning" label={t('parts_sales.marginVatScheme')} sx={{ height: 20, fontSize: 10.5 }} />
                              )}
                              {returned && <Chip size="small" label={t('parts_sales.returned')} sx={{ height: 20, fontSize: 10.5 }} />}
                              {l.target_label && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  icon={<DirectionsCarIcon sx={{ fontSize: 13 }} />}
                                  label={l.target_label}
                                  sx={{ height: 20, fontSize: 10.5, ...plateFont }}
                                  data-testid="lahete-line-target"
                                />
                              )}
                            </Box>
                            {(l.part_code || discountCaption(l)) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ...codeFont, display: 'block' }}
                                data-testid="lahete-line-caption"
                              >
                                {[l.part_code, discountCaption(l)].filter(Boolean).join(' · ')}
                              </Typography>
                            )}
                            {l.part_id && rowNote(l) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                                data-testid="lahete-line-note"
                              >
                                {rowNote(l)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {(() => {
                              const pendingSpecial =
                                l.fulfillment_status && l.fulfillment_status !== 'stocked' && !l.handed_over_at && !returned;
                              if (pendingSpecial) {
                                const chipColor =
                                  l.fulfillment_status === 'arrived' ? 'success'
                                    : l.fulfillment_status === 'ordered' ? 'info'
                                    : 'default';
                                return (
                                  <>
                                    <Chip
                                      size="small"
                                      color={chipColor}
                                      variant={l.fulfillment_status === 'arrived' ? 'filled' : 'outlined'}
                                      label={t(`parts_sales.fulfillment_${l.fulfillment_status}`)}
                                      sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                                      data-testid={`lahete-fulfillment-${l.fulfillment_status}`}
                                    />
                                    {l.fulfillment_status === 'ordered' && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                        {l.supplier_order_id ? (
                                          <Link
                                            component={RouterLink}
                                            to={`/supplier-orders?order=${l.supplier_order_id}`}
                                            underline="hover"
                                            data-testid="lahete-line-supplier-order"
                                          >
                                            {l.supplier_reference || t('parts_sales.openSupplierOrder')}
                                          </Link>
                                        ) : (
                                          l.supplier_reference
                                        )}
                                        {l.expected_date ? ` · ${t('parts_sales.expectedShort')} ${formatDate(l.expected_date)}` : ''}
                                      </Typography>
                                    )}
                                    {l.fulfillment_status === 'arrived' && (
                                      <Typography variant="caption" color={l.customer_notified_at ? 'success.main' : 'text.disabled'} sx={{ display: 'block', mt: 0.25 }}>
                                        {l.customer_notified_at
                                          ? t('parts_sales.smsSentShort')
                                          : t('parts_sales.smsNotSentShort')}
                                      </Typography>
                                    )}
                                  </>
                                );
                              }
                              return (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                    <ScheduleIcon sx={{ fontSize: 14 }} />
                                    <Typography variant="caption">
                                      {l.handed_over_at ? formatDate(l.handed_over_at) : '—'}
                                    </Typography>
                                  </Box>
                                  {l.stock_tracked && l.handed_over_at ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: returned ? 'text.disabled' : 'secondary.main' }}>
                                      <Inventory2OutlinedIcon sx={{ fontSize: 13 }} />
                                      <Typography variant="caption" sx={{ ...numFont }}>
                                        {returned ? t('parts_sales.stockReturned') : `−${formatQuantity(l.quantity)}`}
                                      </Typography>
                                    </Box>
                                  ) : !l.stock_tracked ? (
                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                                      {t('parts_sales.noStockTracking')}
                                    </Typography>
                                  ) : null}
                                </>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {isOpen && !returned ? (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                                <IconButton size="small" onClick={() => stepQty(l, -1)} disabled={parseFloat(l.quantity) <= 1} aria-label={t('parts_sales.decreaseQty')}>
                                  <RemoveIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                                <Typography sx={{ width: 30, textAlign: 'center', ...numFont, fontWeight: 600, fontSize: 13.5 }}>
                                  {formatQuantity(l.quantity)}
                                </Typography>
                                <IconButton size="small" onClick={() => stepQty(l, 1)} aria-label={t('parts_sales.increaseQty')}>
                                  <AddIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ ...numFont }}>{formatQuantity(l.quantity)}</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ ...numFont, whiteSpace: 'nowrap' }}>
                            {(() => {
                              const d = serverLineDisplay(l, orgDefaultVat);
                              return (
                                <>
                                  {formatPriceWithCurrency(d.unit)}
                                  {d.inclVat !== orgDefaultVat && (
                                    <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                      {vatSuffix(d.inclVat)}
                                    </Typography>
                                  )}
                                </>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right" sx={{ ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatPriceWithCurrency(serverLineDisplay(l, orgDefaultVat).total)}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 160 }}>
                            {(() => {
                              // Fulfillment transitions work on invoiced sales too — a
                              // backordered part can arrive after the koontilasku went out.
                              if (returned || isCancelled) {
                                return !isOpen ? <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} /> : null;
                              }
                              let fulfillmentAction = null;
                              // A line that orders through a supplier integration is ordered from
                              // the banner above (or Odottavat); the manual mark stays for the rest
                              // and for lines the seller took out of the queue.
                              const ordersViaSupplier =
                                canOrderFromSuppliers &&
                                l.supplier_integration_id &&
                                l.supplier_supports_ordering !== false &&
                                !l.supplier_order_skipped;
                              if (l.fulfillment_status === 'to_order' && !ordersViaSupplier) {
                                fulfillmentAction = (
                                  <Button size="small" variant="outlined" color="info" onClick={() => openMarkOrdered(l)} data-testid="lahete-mark-ordered" sx={{ borderRadius: '8px', textTransform: 'none', mr: 0.5 }}>
                                    {t('parts_sales.markOrdered')}
                                  </Button>
                                );
                              } else if (l.fulfillment_status === 'ordered') {
                                fulfillmentAction = (
                                  <Button size="small" variant="outlined" color="success" onClick={() => markArrived(l)} data-testid="lahete-mark-arrived" sx={{ borderRadius: '8px', textTransform: 'none', mr: 0.5 }}>
                                    {t('parts_sales.markArrived')}
                                  </Button>
                                );
                              } else if (l.fulfillment_status === 'arrived' && !l.handed_over_at) {
                                fulfillmentAction = (
                                  <Button size="small" variant="contained" color="secondary" onClick={() => handOverLine(l)} data-testid="lahete-hand-over" sx={{ borderRadius: '8px', textTransform: 'none', mr: 0.5 }}>
                                    {t('parts_sales.handOverAction')}
                                  </Button>
                                );
                              }
                              return (
                                <>
                                  {fulfillmentAction}
                                  {isOpen ? (
                                    <>
                                      <Tooltip title={t('parts_sales.editLine')}>
                                        <IconButton size="small" onClick={() => setEditLine(l)} data-testid="lahete-edit-line">
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t('parts_sales.returnLineHint')}>
                                        <IconButton size="small" color="error" onClick={() => returnLine(l.id)} data-testid="lahete-return-line">
                                          <UndoIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    !fulfillmentAction && <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                  )}
                                </>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Box>

        {/* RIGHT rail */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: { lg: 'sticky' }, top: { lg: 24 } }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{t('parts_sales.summary')}</Typography>
            <SumRow label={t('parts_sales.netStandard')} value={formatPriceWithCurrency(parseFloat(sale.total_standard_excl_vat || 0))} />
            {parseFloat(sale.total_margin_incl_vat || 0) > 0 && (
              <SumRow label={t('parts_sales.marginLinesInclVat')} value={formatPriceWithCurrency(parseFloat(sale.total_margin_incl_vat))} muted />
            )}
            <SumRow label={t('parts_sales.vatAmount')} value={formatPriceWithCurrency(parseFloat(sale.total_standard_vat || 0))} muted />
            {totalDiscount > 0 && (
              <SumRow
                label={t('parts_sales.discountTotal')}
                value={`−${formatPriceWithCurrency(totalDiscount)}`}
                muted
              />
            )}
            {parseFloat(sale.total_margin_incl_vat || 0) > 0 && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1.4, pb: 0.5 }}>
                {t('parts_sales.marginExcludedNote')}
              </Typography>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontWeight: 700 }}>{t('parts_sales.total')}</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, ...numFont }} data-testid="lahete-total">
                {formatPriceWithCurrency(parseFloat(sale.total_incl_vat || 0))}
              </Typography>
            </Box>
            {saleMargin?.hasAny && (
              <Box sx={{ mt: 0.5 }} data-testid="lahete-margin">
                <SumRow
                  label={t('parts_sales.marginTotal')}
                  value={
                    saleMargin.percent != null
                      ? `${formatPriceWithCurrency(saleMargin.amount)} (${formatQuantity(saleMargin.percent)} %)`
                      : formatPriceWithCurrency(saleMargin.amount)
                  }
                  muted
                />
                {saleMargin.missingCost > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('parts_sales.marginMissingCostRows', { count: saleMargin.missingCost })}
                  </Typography>
                )}
              </Box>
            )}
            {isOpen && (
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                startIcon={<ReceiptLongIcon />}
                disabled={activeLines.length === 0}
                onClick={() => setInvoiceOpen(true)}
                data-testid="lahete-invoice-summary"
                sx={{ mt: 2, borderRadius: '10px' }}
              >
                {t('parts_sales.invoiceLahete')}
              </Button>
            )}
          </Paper>

          {/* Seller notes */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('parts_sales.targetDefault')}</Typography>
            <TargetLabelField
              value={targetDraft}
              onChange={(v) => {
                setTargetDraft(v);
                setTargetDirty(true);
              }}
              customerId={sale.customer}
              placeholder={t('parts_sales.targetPlaceholder')}
              helperText={t('parts_sales.targetDefaultHelp')}
              disabled={!isOpen}
              inputTestId="lahete-target"
            />
            {targetDirty && isOpen && (
              <Button size="small" startIcon={<SaveIcon />} onClick={saveTarget} sx={{ mt: 1, textTransform: 'none' }} data-testid="lahete-target-save">
                {t('Save')}
              </Button>
            )}
          </Paper>

          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('parts_sales.notesTitle')}</Typography>
            <TextField
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              size="small"
              placeholder={t('parts_sales.notesPlaceholder')}
              value={notesDraft}
              onChange={(e) => {
                setNotesDraft(e.target.value);
                setNotesDirty(true);
              }}
              data-testid="lahete-notes"
            />
            {notesDirty && (
              <Button size="small" startIcon={<SaveIcon />} onClick={saveNotes} sx={{ mt: 1, textTransform: 'none' }} data-testid="lahete-notes-save">
                {t('Save')}
              </Button>
            )}
          </Paper>

          {/* Stock info */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <Inventory2OutlinedIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('parts_sales.handoverStockTitle')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t('parts_sales.handoverStockInfo')}
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Invoice (koontilasku for this single note) */}
      <KoontilaskuDialog
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        saleIds={[sale.id]}
        onDone={(invoice) => {
          setInvoiceOpen(false);
          if (invoice?.id) navigate(`/invoices/${invoice.id}`);
        }}
      />

      <CustomerPurchaseHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        customerId={sale.customer}
        customerName={sale.customer_name}
      />

      {/* Mark ordered (tilattu) */}
      <Dialog open={!!markOrderedLine} onClose={() => setMarkOrderedLine(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle>{t('parts_sales.markOrderedTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('parts_sales.supplierReference')}
              placeholder={t('parts_sales.supplierReferencePlaceholder')}
              value={orderForm.reference}
              onChange={(e) => setOrderForm({ ...orderForm, reference: e.target.value })}
              size="small"
              fullWidth
              autoFocus
              inputProps={{ 'data-testid': 'lahete-order-reference' }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
              <DatePicker
                label={t('parts_sales.expectedDate')}
                value={orderForm.expected ? parseISO(orderForm.expected) : null}
                onChange={(d) =>
                  setOrderForm({ ...orderForm, expected: d && isValid(d) ? formatDateFns(d, 'yyyy-MM-dd') : '' })
                }
                slotProps={{ textField: { size: 'small', sx: { maxWidth: 200 } } }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkOrderedLine(null)}>{t('Cancel')}</Button>
          <Button variant="contained" color="secondary" onClick={submitMarkOrdered} data-testid="lahete-mark-ordered-confirm">
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tilaa toimittajalta (same dialog as the work order's banner) */}
      <SupplierOrderDialog
        open={!!supplierOrderGroup}
        onClose={() => setSupplierOrderGroup(null)}
        partsSale={sale}
        integrationId={supplierOrderGroup?.integrationId}
        integrationName={supplierOrderGroup?.integrationName}
        unorderedParts={dialogRows(supplierOrderGroup?.lines || [])}
        onOrderPlaced={fetchSale}
      />

      {/* Tuo ostoskori (vendor basket → to_order lines) */}
      <LaheteCartImportDialog
        open={cartImportOpen}
        onClose={() => setCartImportOpen(false)}
        sale={sale}
        onImported={async (count) => {
          setCartImportOpen(false);
          await fetchSale();
          setSnack({ open: true, message: t('parts_sales.cartImported', { count }), severity: 'success' });
        }}
      />

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle>{t('parts_sales.cancelLahete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('parts_sales.cancelConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>{t('Cancel')}</Button>
          <Button variant="contained" color="error" onClick={cancelSale} data-testid="lahete-cancel-confirm">
            {t('parts_sales.cancelLahete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit line */}
      <SaleLineDialog
        open={!!editLine}
        onClose={() => setEditLine(null)}
        initialLine={editInitialLine}
        onSubmit={submitLineEdit}
        submitting={submitting}
        customerId={sale.customer}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LahetteDetailView;
